/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import { Dayjs } from 'dayjs';
import { NavLink } from 'react-router-dom';
import { baseCell, currencyCell, DataGridCell } from 'components/cells';
import productStatusCell from 'components/cells/product-status-cell';
import {
  filterOperators,
  numberFilterOperators,
  singleSelectFilterOperators
} from 'components/enhanced';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import { useIsDirector, useIsSupplier, useIsTechnician } from 'shared/permissions';

export function ConsumptionsColumns(startDate: Dayjs, endDate: Dayjs): GridColDef[] {
  const { t } = useTranslation();
  const translationPrefix = 'entities.movement';
  const productLinkDisable = useIsDirector() || useIsSupplier();
  const isTec = useIsTechnician();
  const monthNames = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
  ];
  const columns: GridColDef[] = [];
  columns.push(
    {
      field: 'product',
      headerName: t(`global.product`),
      filterOperators,
      width: 150,
      valueGetter: (params: GridValueGetterParams) => params.row.product,
      renderCell: (params: GridRenderCellParams<any, any>) => {
        if (productLinkDisable) {
          return <span className="text-primary-600">{params.value}</span>;
        }
        return (
          <NavLink to={`../products/${params.row.product_id}`}>
            <b className="text-primary-600">{params.value}</b>
          </NavLink>
        );
      }
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      width: 150,
      type: 'singleSelect',
      filterOperators: singleSelectFilterOperators,
      valueOptions: [
        { value: 'ACTIVE', label: t('entities.product.status_options.ACTIVE') },
        { value: 'DECOMMISSIONED', label: t('entities.product.status_options.DECOMMISSIONED') },
        {
          value: 'TO_BE_DECOMMISSIONED',
          label: t('entities.product.status_options.TO_BE_DECOMMISSIONED')
        },
        { value: 'TENDER', label: t('entities.product.status_options.TENDER') },
        { value: 'BLOCKED', label: t('entities.product.status_options.BLOCKED') }
      ],
      renderCell: productStatusCell(t)
    },
    {
      field: 'product_type',
      renderCell: baseCell,
      headerName: t(`global.product_type`),
      type: 'string',
      width: 100,
      filterOperators
    }
  );
  if (!isTec) {
    columns.push({
      field: 'unit_price',
      headerName: t(`${translationPrefix}.unit_price`),
      type: 'number',
      filterOperators: numberFilterOperators,
      sortable: true,
      width: 100,
      valueFormatter: (value) => currencyFormatter(value, { significantDigits: 3 }),
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
    });
  }

  columns.push(
    {
      field: 'amount',
      type: 'number',
      headerName: t(`${translationPrefix}.amount`),
      filterOperators: numberFilterOperators,
      sortable: true,
      width: 100,
      valueFormatter: (value) => currencyFormatter(value, { significantDigits: 3 }),
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
    },
    {
      field: 'avg',
      type: 'number',
      headerName: t(`${translationPrefix}.avg_quantity`),
      filterOperators: numberFilterOperators,
      sortable: true,
      width: 100,
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <b className="text-primary-600"> {params.value} </b>
      )
    },
    {
      field: 'tot',
      type: 'number',
      headerName: t(`${translationPrefix}.total`),
      filterOperators: numberFilterOperators,
      sortable: true,
      width: 100,
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <b className="text-primary-600"> {params.value} </b>
      )
    },
    {
      field: 'label',
      type: 'string',
      renderCell: baseCell,
      headerName: t(`global.label`),
      width: 250
    },
    {
      field: 'description',
      type: 'string',
      headerName: t(`global.description`),
      width: 250,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'supplier',
      type: 'string',
      headerName: t(`global.supplier`),
      width: 250,
      renderCell: baseCell
    },
    {
      field: 'notes',
      type: 'string',
      headerName: t(`global.notes`),
      width: 250,
      filterOperators,
      sortable: true,
      renderCell: baseCell
    },
    {
      field: 'category',
      type: 'string',
      headerName: t(`global.category`),
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      filterOperators,
      sortable: true,
      width: 250,
      renderCell: baseCell
    }
  );

  if (endDate && startDate) {
    for (
      let currentDate = startDate;
      endDate.isAfter(currentDate);
      currentDate = currentDate.add(1, 'month')
    ) {
      const year = currentDate.year();
      const month = currentDate.month();

      columns.push({
        field: `m_${month}__y_${year}`,
        type: 'number',
        headerName: `${t(`months.${monthNames[month]}`)} ${year}`,
        filterable: false,
        sortable: false,
        width: 100,
        renderCell: (params: GridRenderCellParams<any, any>) => (
          <DataGridCell content={params.value} />
        )
      });
    }
  }

  return columns;
}
